import React, { useState, useEffect } from 'react';
import { Card, Button, Typography, Alert, Skeleton, App, Dropdown, MenuProps } from 'antd';
import {
  PlusOutlined,
  LockOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  PlusCircleOutlined,
  MinusCircleOutlined,
  CalculatorOutlined,
  DownOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { useResponsive } from '../../hooks/useResponsive';
import { useAppSelector } from '../../hooks/redux';
import { useCurrency } from '../../hooks/useCurrency';
import { useRestaurantTimeZone } from '../../hooks/useTimeZone';
import {
  useGetActiveCashRegistersQuery,
  useOpenCashRegisterMutation,
  useCloseCashRegisterMutation,
  useAddCashMovementMutation,
} from '../../services/cashRegisterApi';
import { useCreateShiftClosingMutation } from '../../services/shiftClosingApi';
import { CashRegisterDto, CreateShiftClosingDto } from '../../types/cashRegister';
import {
  shouldHideCashTotals,
  canSeeCashMovements,
  canCloseCashRegister,
  canDoCashCount,
  canDoShiftClosing,
} from '../../utils/roleHelpers';
import CashRegisterCard, { getOperatingTime } from './CashRegisterCard';
import './cashRegister.css';
import OpenCashRegisterModal from './OpenCashRegisterModal';
import CloseCashRegisterModal from './CloseCashRegisterModal';
import AddCashMovementModal from './AddCashMovementModal';
import CashCountModal from './CashCountModal';
import CashMovementsList from './CashMovementsList';
import CreateShiftClosingModal from './CreateShiftClosingModal';
import RejectedShiftClosingAlert from './RejectedShiftClosingAlert';
import { printAgentService } from '../../services/printAgentService';
import { bluetoothPrintService } from '../../services/bluetoothPrintService';
import { useGetBoolSettingQuery } from '../../services/settingsApi';
import { useCloudPrint } from '../../hooks/useCloudPrint';

const { Title, Text } = Typography;

const CashRegisterManagement: React.FC = () => {
  const { message, modal } = App.useApp();
  const { isMobile } = useResponsive();
  const { currentRestaurant } = useAppSelector((state) => state.restaurant);
  const { user } = useAppSelector((state) => state.auth);
  const { currencySymbol } = useCurrency();
  const { formatInTimeZone } = useRestaurantTimeZone();

  // Cash access rules centralized in roleHelpers — blind counting depends on them
  const shouldHideTotals = shouldHideCashTotals(user?.role);
  const canSeeMovements = canSeeCashMovements(user?.role);
  const canClose = canCloseCashRegister(user?.role);
  const canArqueo = canDoCashCount(user?.role);
  const canShiftClose = canDoShiftClosing(user?.role);

  const [openModalVisible, setOpenModalVisible] = useState(false);
  const [closeModalVisible, setCloseModalVisible] = useState(false);
  const [selectedCashRegister, setSelectedCashRegister] = useState<CashRegisterDto | null>(null);
  const [movementModalVisible, setMovementModalVisible] = useState(false);
  const [movementType, setMovementType] = useState<'income' | 'expense' | null>(null);
  const [cashCountModalVisible, setCashCountModalVisible] = useState(false);
  const [shiftClosingModalVisible, setShiftClosingModalVisible] = useState(false);
  const [printAgentAvailable, setPrintAgentAvailable] = useState(false);
  const [bluetoothAvailable, setBluetoothAvailable] = useState(false);

  // Re-render every minute so the "Abierta · Xh Ym" chip stays live
  const [, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 60000);
    return () => clearInterval(interval);
  }, []);

  // Cierre de Turno — hidden by default; gated by the CierreTurnoActivo setting
  const { data: cierreTurnoSetting } = useGetBoolSettingQuery('CierreTurnoActivo');
  const isCierreTurnoEnabled = cierreTurnoSetting?.value ?? false;

  // Cloud print
  const { data: cloudPrintSetting } = useGetBoolSettingQuery('CloudPrintEnabled');
  const cloudPrintEnabled = cloudPrintSetting?.value ?? false;
  const { printJob: cloudPrintJob } = useCloudPrint();

  // Check for available print services when movement modal opens (same pattern as ComandearModal)
  useEffect(() => {
    const checkPrintServices = async () => {
      // Check Web Bluetooth API (Android/Chrome)
      const btAvailable = bluetoothPrintService.isAvailable();
      setBluetoothAvailable(btAvailable);

      // Check Print Agent (Windows)
      const agentAvailable = await printAgentService.isAvailable();
      setPrintAgentAvailable(agentAvailable);
    };
    if (movementModalVisible) {
      checkPrintServices();
    }
  }, [movementModalVisible]);

  const { data: cashRegister, isLoading, error, refetch } = useGetActiveCashRegistersQuery(undefined, {
    skip: !currentRestaurant?.id
  });

  const [openCashRegister, { isLoading: isOpening }] = useOpenCashRegisterMutation();
  const [closeCashRegister, { isLoading: isClosing }] = useCloseCashRegisterMutation();
  const [addCashMovement, { isLoading: isAddingMovement }] = useAddCashMovementMutation();
  const [createShiftClosing, { isLoading: isCreatingShiftClosing }] = useCreateShiftClosingMutation();

  const handleOpenCashRegister = async (initialAmount: number) => {
    if (!user?.id) return;

    try {
      await openCashRegister({
        initialAmount,
      }).unwrap();

      message.success('Caja abierta exitosamente');
      setOpenModalVisible(false);
      refetch();
    } catch (error: any) {
      const errorMessage = error?.data?.message
        || error?.message
        || 'Error al abrir la caja registradora';
      message.error(errorMessage, 5);
      console.error('Error opening cash register:', error);
    }
  };

  const handleCloseCashRegister = (cashReg: CashRegisterDto) => {
    setSelectedCashRegister(cashReg);
    setCloseModalVisible(true);
  };

  const confirmCloseCashRegister = async (finalAmount: number, notes?: string) => {
    if (!selectedCashRegister) return;

    try {
      await closeCashRegister({
        registerId: selectedCashRegister.id,
        data: {
          finalAmount,
          notes,
        }
      }).unwrap();

      message.success('Caja cerrada exitosamente');
      setCloseModalVisible(false);
      setSelectedCashRegister(null);
      refetch();
    } catch (error: any) {
      const errorMessage = error?.data?.message
        || error?.message
        || 'Error al cerrar la caja registradora';
      message.error(errorMessage, 5);
      console.error('Error closing cash register:', error);
    }
  };

  const handleQuickClose = (cashRegister: CashRegisterDto) => {
    const expectedAmount = cashRegister.expectedAmount ?? cashRegister.summary?.expectedAmount ?? 0;

    modal.confirm({
      title: '¿Cerrar caja registradora?',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>¿Está seguro que desea cerrar la caja de {cashRegister.userName}?</p>
          <p><strong>Monto esperado:</strong> {currencySymbol} {expectedAmount.toFixed(2)}</p>
          <p style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
            El cierre rápido asume que el monto final es igual al monto esperado.
          </p>
        </div>
      ),
      okText: 'Sí, cerrar',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await closeCashRegister({
            registerId: cashRegister.id,
            data: {
              finalAmount: expectedAmount,
              notes: 'Cierre rápido automático',
            }
          }).unwrap();

          message.success('Caja cerrada exitosamente');
          refetch();
        } catch (error: any) {
          console.error('Error in quick close:', error);
          const errorMessage = error?.data?.message
            || error?.message
            || 'Error al cerrar la caja registradora';
          message.error(errorMessage, 5);
        }
      },
    });
  };

  const handleAddIncome = () => {
    setMovementType('income');
    setMovementModalVisible(true);
  };

  const handleAddExpense = () => {
    setMovementType('expense');
    setMovementModalVisible(true);
  };

  const handleSubmitMovement = async (values: { amount: number; reason: string; reference?: string; type: string }) => {
    if (!activeCashRegister?.id) {
      message.error('No hay caja registradora activa');
      return;
    }

    try {
      await addCashMovement({
        registerId: activeCashRegister.id,
        data: {
          cashRegisterId: activeCashRegister.id,
          type: values.type,
          amount: values.amount,
          reason: values.reason,
          reference: values.reference,
        }
      }).unwrap();

      message.success(
        movementType === 'income'
          ? 'Ingreso registrado exitosamente'
          : 'Retiro registrado exitosamente'
      );
      setMovementModalVisible(false);
      setMovementType(null);
      refetch();
    } catch (error: any) {
      const errorMessage = error?.data?.message
        || error?.message
        || 'Error al registrar el movimiento';
      message.error(errorMessage, 5);
      console.error('Error adding cash movement:', error);
    }
  };

  const handleSubmitMovementAndPrint = async (values: { amount: number; reason: string; reference?: string; type: string }) => {
    if (!activeCashRegister?.id) {
      message.error('No hay caja registradora activa');
      return;
    }

    try {
      // 1. Save the movement
      await addCashMovement({
        registerId: activeCashRegister.id,
        data: {
          cashRegisterId: activeCashRegister.id,
          type: values.type,
          amount: values.amount,
          reason: values.reason,
          reference: values.reference,
        }
      }).unwrap();

      // 2. Print the receipt using same pattern as ComandearModal
      const userName = user ? `${user.firstName} ${user.lastName}` : 'Usuario';
      const restaurantName = currentRestaurant?.name || 'MASSRESTAURANT';
      const movType = values.type as 'CORRECTION' | 'DROP';

      // Try Cloud Print first (if enabled)
      if (cloudPrintEnabled) {
        // Field names must match the backend EscPosReceiptData DTO exactly
        // (JSON binding is case-insensitive but does not translate names).
        // GenerateCashMovementReceipt reads RestaurantName/Title/WaiterName/DateTime/Total/Footer.
        const typeLabel = movType === 'CORRECTION' ? 'INGRESO' : 'RETIRO';
        await cloudPrintJob({
          printType: 'CashMovement',
          receiptData: {
            restaurantName: restaurantName,
            title: 'MOVIMIENTO DE CAJA',
            tableName: 'CAJA',
            waiterName: userName,
            total: values.amount,
            footer: `Tipo: ${typeLabel}\nMotivo: ${values.reason}${values.reference ? `\nReferencia: ${values.reference}` : ''}\n\nComprobante de Movimiento`,
          },
        });
        message.success(
          movementType === 'income'
            ? 'Ingreso registrado e impreso exitosamente'
            : 'Retiro registrado e impreso exitosamente'
        );
      }
      // Try Print Agent (Windows)
      else if (printAgentAvailable) {
        const result = await printAgentService.printCashMovement(
          restaurantName,
          userName,
          movType,
          values.amount,
          values.reason,
          values.reference
        );

        if (result.success) {
          message.success(
            movementType === 'income'
              ? 'Ingreso registrado e impreso exitosamente'
              : 'Retiro registrado e impreso exitosamente'
          );
        } else {
          message.warning(`Movimiento registrado, pero error al imprimir: ${result.message}`);
        }
      }
      // Try Bluetooth if Print Agent not available (Android/Chrome)
      else if (bluetoothAvailable) {
        const result = await bluetoothPrintService.printCashMovement(
          restaurantName,
          userName,
          movType,
          values.amount,
          values.reason,
          values.reference
        );

        if (result.success) {
          message.success(
            movementType === 'income'
              ? 'Ingreso registrado e impreso exitosamente'
              : 'Retiro registrado e impreso exitosamente'
          );
        } else {
          message.warning(`Movimiento registrado, pero error al imprimir: ${result.message}`);
        }
      }
      // No print service available
      else {
        console.warn('⚠️ [CashMovement] No print service available');
        message.warning('Movimiento registrado. No hay servicio de impresión disponible.');
      }

      setMovementModalVisible(false);
      setMovementType(null);
      refetch();
    } catch (error: any) {
      const errorMessage = error?.data?.message
        || error?.message
        || 'Error al registrar el movimiento';
      message.error(errorMessage, 5);
      console.error('Error adding cash movement:', error);
    }
  };

  const handleCashCount = () => {
    setCashCountModalVisible(true);
  };

  const handleConfirmCashCount = async () => {
    setCashCountModalVisible(false);
    message.success('Arqueo registrado correctamente');
  };

  const handleCreateShiftClosing = async (values: CreateShiftClosingDto) => {
    if (!activeCashRegister?.id) {
      message.error('No hay caja registradora activa');
      return;
    }

    try {
      await createShiftClosing({
        cashRegisterId: activeCashRegister.id,
        data: values,
      }).unwrap();

      setShiftClosingModalVisible(false);

      // Force refetch to update the UI with PendingReview status
      // The mutation should automatically invalidate tags, but we force refetch to ensure UI updates
      const result = await refetch();

      if (result.isSuccess) {
        message.success('Cierre de turno creado. Esperando revisión del administrador.');
      }
    } catch (error: any) {
      const errorMessage = error?.data?.message
        || error?.message
        || 'Error al crear cierre de turno';
      message.error(errorMessage, 5);
      console.error('Error creating shift closing:', error);
    }
  };

  // Check if error is a real error or just "no active register" (404)
  const isRealError = error && 'status' in error && error.status !== 404 && error.status !== 'PARSING_ERROR';

  if (isRealError) {
    return (
      <Alert
        message="Error al cargar cajas registradoras"
        description="Ocurrió un error al intentar cargar la información de las cajas registradoras"
        type="error"
        showIcon
        style={{ marginBottom: 24 }}
      />
    );
  }

  // The API now returns a single active register for the current user, not an array
  const activeCashRegister = cashRegister && (cashRegister.status === 'Open' || cashRegister.status === 'PendingReview') ? cashRegister : null;
  const hasActiveRegister = !!activeCashRegister;
  const isPendingReview = activeCashRegister?.status === 'PendingReview';

  // One "Cerrar Caja" button; the variants live inside the menu so the dangerous
  // quick-close is a deliberate second step, not a sibling of Arqueo.
  const closeMenuItems: MenuProps['items'] = [
    ...(canClose ? [
      {
        key: 'count',
        label: 'Cerrar con conteo',
        icon: <CalculatorOutlined />,
        onClick: () => activeCashRegister && handleCloseCashRegister(activeCashRegister),
      },
      {
        key: 'quick',
        label: 'Cierre rápido (sin contar)',
        icon: <LockOutlined />,
        danger: true,
        onClick: () => activeCashRegister && handleQuickClose(activeCashRegister),
      },
    ] : []),
    ...(isCierreTurnoEnabled && canShiftClose && !isPendingReview ? [
      ...(canClose ? [{ type: 'divider' as const }] : []),
      {
        key: 'shift',
        label: 'Cerrar turno (con revisión)',
        icon: <CheckCircleOutlined />,
        onClick: () => setShiftClosingModalVisible(true),
      },
    ] : []),
  ];

  return (
    <div>
      {/* Alert for rejected shift closings */}
      <RejectedShiftClosingAlert />

      {/* Alert for PendingReview status */}
      {activeCashRegister?.status === 'PendingReview' && (
        <Alert
          message="Turno en Revisión"
          description="Tu cierre de turno está siendo revisado por un administrador. No puedes realizar movimientos hasta que sea aprobado o rechazado."
          type="warning"
          showIcon
          closable
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Single bar: identity + live status chip + the day's actions.
          The old title/subtitle and the three stat cards said "Abierta" three
          different ways — everything lives here now. */}
      <div className="cash-bar">
        <h2 className="cash-bar__title">
          {hasActiveRegister ? `Caja de ${activeCashRegister!.userName}` : 'Mi Caja'}
        </h2>
        {hasActiveRegister && (
          <span className="cash-bar__chip">
            <span className={`cash-dot${isPendingReview ? ' cash-dot--warn' : ''}`} />
            {isPendingReview ? 'En revisión' : 'Abierta'}
            {' · '}<b>{getOperatingTime(activeCashRegister!.openTime)}</b>
            {' · '}desde {formatInTimeZone(activeCashRegister!.openTime, 'HH:mm')}
          </span>
        )}
        <div className="cash-bar__spacer" />
        {hasActiveRegister && (
          <div className="cash-bar__actions">
            {!isPendingReview && (
              <>
                <Button className="cash-btn-income" icon={<PlusCircleOutlined />} onClick={handleAddIncome}>
                  Ingreso
                </Button>
                <Button className="cash-btn-expense" icon={<MinusCircleOutlined />} onClick={handleAddExpense}>
                  Retiro
                </Button>
              </>
            )}
            {canArqueo && !isPendingReview && (
              <Button icon={<CalculatorOutlined />} onClick={handleCashCount}>
                Arqueo
              </Button>
            )}
            {(canClose || (isCierreTurnoEnabled && canShiftClose)) && (
              <Dropdown menu={{ items: closeMenuItems }} trigger={['click']}>
                <Button type="primary" icon={<LockOutlined />} loading={isClosing || isCreatingShiftClosing}>
                  Cerrar Caja <DownOutlined style={{ fontSize: 10 }} />
                </Button>
              </Dropdown>
            )}
            {isPendingReview && (
              <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isLoading}>
                Actualizar Estado
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="cash-cols">
          <Card><Skeleton active paragraph={{ rows: 4 }} /></Card>
          <Card><Skeleton active paragraph={{ rows: 4 }} /></Card>
        </div>
      ) : !activeCashRegister ? (
        <Card style={{ textAlign: 'center', padding: '40px 20px' }}>
          <LockOutlined style={{ fontSize: '48px', color: 'var(--border-color-strong)', marginBottom: 16 }} />
          <Title level={4} style={{ color: 'var(--color-neutral-text)' }}>
            No tienes una caja registradora abierta
          </Title>
          <Text type="secondary" style={{ fontSize: isMobile ? '14px' : '16px' }}>
            Abre una caja registradora para comenzar a operar
          </Text>
          <div style={{ marginTop: 24 }}>
            <Button
              type="primary"
              size="large"
              icon={<PlusOutlined />}
              onClick={() => setOpenModalVisible(true)}
            >
              Abrir Mi Caja
            </Button>
          </div>
        </Card>
      ) : (
        <div className={`cash-cols${canSeeMovements ? '' : ' cash-cols--single'}`}>
          <CashRegisterCard
            cashRegister={activeCashRegister}
            hideTotals={shouldHideTotals}
          />
          {canSeeMovements && (
            <CashMovementsList cashRegisterId={activeCashRegister.id} variant="card" />
          )}
        </div>
      )}

      {/* Modals */}
      <OpenCashRegisterModal
        visible={openModalVisible}
        onCancel={() => setOpenModalVisible(false)}
        onConfirm={handleOpenCashRegister}
        loading={isOpening}
      />

      <CloseCashRegisterModal
        visible={closeModalVisible}
        cashRegister={selectedCashRegister}
        onCancel={() => {
          setCloseModalVisible(false);
          setSelectedCashRegister(null);
        }}
        onConfirm={confirmCloseCashRegister}
        loading={isClosing}
      />

      <AddCashMovementModal
        visible={movementModalVisible}
        movementType={movementType}
        onCancel={() => {
          setMovementModalVisible(false);
          setMovementType(null);
        }}
        onSubmit={handleSubmitMovement}
        onSubmitAndPrint={handleSubmitMovementAndPrint}
        loading={isAddingMovement}
        cashRegister={activeCashRegister}
      />

      <CashCountModal
        visible={cashCountModalVisible}
        expectedAmount={activeCashRegister?.expectedAmount ?? 0}
        cashRegister={activeCashRegister}
        onCancel={() => setCashCountModalVisible(false)}
        onConfirm={handleConfirmCashCount}
      />

      {/* Shift Closing Modal */}
      <CreateShiftClosingModal
        visible={shiftClosingModalVisible}
        cashRegisterId={activeCashRegister?.id || ''}
        onCancel={() => setShiftClosingModalVisible(false)}
        onSubmit={handleCreateShiftClosing}
        loading={isCreatingShiftClosing}
      />
    </div>
  );
};

export default CashRegisterManagement;